// ----------------------------------------------------------------------
// Pure schema.org builders. Every value comes from data the page already
// renders; nothing here invents ratings, prices, or availability.

import { AppUrl } from '../../config';

const ORGANIZATION = {
  '@type': 'EducationalOrganization',
  name: 'ASAP Marketing and Studies Consultancy',
  url: AppUrl,
};

const AREA_SERVED = [
  { '@type': 'Country', name: 'United Arab Emirates' },
  { '@type': 'Country', name: 'India' },
];

const absolute = (value) => {
  if (!value) {
    return undefined;
  }

  return value.startsWith('http') ? value : `${AppUrl}${value}`;
};

const pageUrl = (path) => {
  if (!path || path === '/') {
    return `${AppUrl}/`;
  }

  const withLeading = path.startsWith('/') ? path : `/${path}`;

  return `${AppUrl}${withLeading.endsWith('/') && withLeading !== '/' ? withLeading.slice(0, -1) : withLeading}`;
};

export function courseSchema({ name, description, image, path }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name,
    description,
    image: absolute(image),
    url: pageUrl(path),
    provider: ORGANIZATION,
  };
}

export function serviceSchema({ name, description, path }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name,
    description,
    url: pageUrl(path),
    provider: ORGANIZATION,
    areaServed: AREA_SERVED,
  };
}

export function collectionSchema({ name, description, path, items = [] }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name,
    description,
    url: pageUrl(path),
    isPartOf: { '@id': `${AppUrl}/#organization` },
    mainEntity: items.length
      ? {
          '@type': 'ItemList',
          numberOfItems: items.length,
          itemListElement: items.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            // Items without their own route (for example achievers) still get a
            // list entry, just without a url.
            ...(item.path ? { url: pageUrl(item.path) } : {}),
          })),
        }
      : undefined,
  };
}

export function breadcrumbSchema(trail = []) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: trail.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: pageUrl(item.path),
    })),
  };
}

export function articleSchema({
  headline,
  description,
  image,
  path,
  datePublished,
  dateModified,
  authorName,
  authorJobTitle,
  section,
  keywords,
  wordCount,
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline,
    description,
    image: absolute(image),
    mainEntityOfPage: pageUrl(path),
    datePublished,
    dateModified,
    articleSection: section,
    keywords,
    wordCount,
    author: {
      '@type': 'Person',
      name: authorName,
      jobTitle: authorJobTitle,
    },
    publisher: ORGANIZATION,
  };
}
